import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Award, Heart, Rocket, ArrowRight, Users, ChevronDown } from 'lucide-react';

const HeroSection = ({ scrollToSection }) => {
  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-black via-gray-900 to-black pt-20">
      {/* Animated Background with subtle elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Glowing orbs */}
        <motion.div
          animate={{
            scale: [1, 1.3, 1],
            x: [0, 100, 0],
            y: [0, -50, 0],
          }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/4 right-1/4 w-[600px] h-[600px] bg-[#fbb040] rounded-full blur-[150px] opacity-20"
        />
        <motion.div
          animate={{
            scale: [1, 1.5, 1],
            x: [0, -100, 0],
            y: [0, 50, 0],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-1/4 left-1/4 w-[500px] h-[500px] bg-[#1f2d5a] rounded-full blur-[120px] opacity-30"
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            {/* Animated Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: "spring", duration: 0.8 }}
            >
              <motion.div 
                className="inline-flex items-center gap-3 bg-gradient-to-r from-[#fbb040] via-yellow-400 to-[#fbb040] text-black px-6 py-3 rounded-full text-sm font-black mb-6 shadow-2xl border-2 border-yellow-300"
                animate={{ 
                  y: [0, -10, 0],
                  boxShadow: [
                    '0 10px 40px rgba(251, 176, 64, 0.4)',
                    '0 20px 60px rgba(251, 176, 64, 0.6)',
                    '0 10px 40px rgba(251, 176, 64, 0.4)'
                  ]
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <span className="text-lg">¡LOGRA TUS METAS!</span>
              </motion.div>
              
              {/* Explosive Title */}
              <motion.h1 
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative"
              >
                <div className="text-6xl md:text-7xl lg:text-8xl font-black leading-tight mb-6">
                  <motion.span
                    animate={{ 
                      color: ['#ffffff', '#fbb040', '#ffffff'],
                    }}
                    transition={{ duration: 3, repeat: Infinity }}
                  >
                    Tu futuro
                  </motion.span>
                  <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#fbb040] via-yellow-300 to-[#fbb040] animate-gradient">
                    empieza HOY
                  </span>
                </div>
              </motion.h1>
            </motion.div>

            {/* Description */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <p className="text-2xl md:text-3xl text-gray-300 font-bold relative inline-block">
                La primera plataforma{' '}
                <span className="relative inline-block">
                  <span className="text-[#fbb040] font-black">de finanzas con propósito</span>
                  <motion.span
                    className="absolute bottom-0 left-0 w-full h-1 bg-[#fbb040]"
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 1, delay: 1 }}
                  />
                </span>
              </p>
            </motion.div>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-6"
            >
              <motion.button
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: "0 0 40px rgba(251, 176, 64, 0.8)",
                }}
                whileTap={{ scale: 0.95 }}
                onClick={() => scrollToSection('planes')}
                className="group relative overflow-hidden bg-gradient-to-r from-[#fbb040] via-yellow-400 to-[#fbb040] text-black px-10 py-6 rounded-2xl font-black text-xl shadow-2xl flex items-center justify-center gap-3"
              >
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-yellow-300 to-[#fbb040]"
                  initial={{ x: '-100%' }}
                  whileHover={{ x: '100%' }}
                  transition={{ duration: 0.6 }}
                />
                <span className="relative z-10 flex items-center gap-3">
                  <motion.div
                    animate={{ y: [0, -8, 0] }}
                    transition={{ duration: 0.8, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <Rocket size={28} />
                  </motion.div>
                  ¡QUIERO MI PLAN AHORA!
                  <ArrowRight size={28} className="group-hover:translate-x-2 transition-transform" />
                </span>
              </motion.button>
            </motion.div>

            {/* Feature Pills */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="flex flex-wrap gap-4 pt-4"
            >
              {[
                { icon: CheckCircle, text: 'Ahorra con propósito', gradient: 'from-green-400 to-emerald-500' },
                { icon: Award, text: 'Hasta 15% de vuelta*', gradient: 'from-[#fbb040] to-yellow-500' },
                { icon: Heart, text: '100% indexado', gradient: 'from-red-400 to-pink-500' },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1 + index * 0.1, type: "spring" }}
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  className={`flex items-center gap-2 bg-gradient-to-r ${item.gradient} text-white px-5 py-3 rounded-full shadow-xl font-bold border-2 border-white/30`}
                >
                  <item.icon size={22} />
                  <span>{item.text}</span>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Right Side - Stats Card with Kompaisito */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, x: 100 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative flex items-center justify-center -mt-12"
          >
            <div className="relative flex flex-col items-center scale-75 md:scale-90 lg:scale-100">
              {/* Users Stats Card on top - Responsive sizing */}
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.8, type: "spring" }}
                className="relative z-10 bg-gradient-to-br from-[#fbb040] to-yellow-500 p-4 md:p-6 lg:p-7 rounded-2xl md:rounded-3xl shadow-2xl border-2 md:border-4 border-yellow-300 mb-[-4px]"
              >
                <div className="flex flex-col items-center gap-2 md:gap-3">
                  <motion.div
                    animate={{ 
                      scale: [1, 1.1, 1],
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="w-16 h-16 md:w-20 md:h-20 lg:w-[84px] lg:h-[84px] bg-black rounded-xl md:rounded-2xl flex items-center justify-center shadow-xl"
                  >
                    <Users className="text-[#fbb040]" size={32} strokeWidth={2.5} />
                  </motion.div>
                  
                  <div className="text-center">
                    <motion.p 
                      animate={{ scale: [1, 1.05, 1] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                      className="text-3xl md:text-4xl lg:text-[2.625rem] font-black text-black mb-1 leading-none"
                    >
                      +8,000
                    </motion.p>
                    <p className="text-sm md:text-base lg:text-[1.19rem] font-black text-black/80">Usuarios registrados</p>
                  </div>
                </div>
              </motion.div>

              {/* Kompaisito below box - Responsive sizing, 50% more separation */}
              <motion.img
                src="https://customer-assets.emergentagent.com/job_f2ee6b04-88a4-4dce-acdb-9e470155c901/artifacts/sqmsw1b6_edited-photo%282%29.png"
                alt="Kompaisito"
                animate={{ 
                  y: [0, -30, 0],
                }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                className="w-[346px] h-[346px] z-20 drop-shadow-2xl"
                style={{ transform: 'scaleY(2.778)' }}
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Animated Scroll Indicator */}
      <motion.div
        animate={{ 
          y: [0, 15, 0],
          opacity: [1, 0.5, 1],
        }}
        transition={{ duration: 2, repeat: Infinity }}
        className="absolute bottom-12 left-[42%] md:left-1/2 transform -translate-x-1/2 cursor-pointer"
        onClick={() => scrollToSection('mantra')}
      >
        <div className="flex flex-col items-center gap-2">
          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 1, repeat: Infinity }}
            className="w-12 h-12 md:w-16 md:h-16 bg-[#fbb040] rounded-full flex items-center justify-center shadow-xl"
          >
            <ChevronDown size={24} className="md:hidden text-black font-bold" />
            <ChevronDown size={32} className="hidden md:block text-black font-bold" />
          </motion.div>
          <span className="text-[#fbb040] font-bold text-xs md:text-sm text-center">Descubre más</span>
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;